import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Row, Col, Form, Button, Spinner, ButtonGroup } from 'react-bootstrap';
import CompanyService from '../../../services/companyService';
import receptionService from '../../../services/receptionService';
import {
  buildJournalVisitFilters,
  buildJournalVisitQuery,
  JOURNAL_PAGE_SIZE,
  defaultJournalRangeIsoDates,
  defaultJournalMonthRangeIsoDates,
  isoDateLocal
} from '../journalVisitQuery';
import VisitHistoryTableRows from './VisitHistoryTableRows';

export default function ReceptionVisitHistoryPanel({
  toast,
  panelTitle,
  panelSubtitle,
  defaultServiceLine = 'all',
  lockServiceLine = false,
  hidePanelHeader = false
}) {
  const [journalScope, setJournalScope] = useState('today');
  const [journalRangeFrom, setJournalRangeFrom] = useState('');
  const [journalRangeTo, setJournalRangeTo] = useState('');
  const [journalCompanyId, setJournalCompanyId] = useState('all');
  const [journalCompanies, setJournalCompanies] = useState([]);
  const [journalRows, setJournalRows] = useState([]);
  const [journalTotal, setJournalTotal] = useState(0);
  const [journalPage, setJournalPage] = useState(0);
  const [journalLoading, setJournalLoading] = useState(true);
  const [journalLine, setJournalLine] = useState(defaultServiceLine);
  const [journalSearch, setJournalSearch] = useState('');
  const [journalSearchDebounced, setJournalSearchDebounced] = useState('');
  const paginationWarnedRef = useRef(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setJournalSearchDebounced(journalSearch);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [journalSearch]);

  const journalExtraFilters = useMemo(
    () => ({
      ...(lockServiceLine && defaultServiceLine !== 'all'
        ? { service_line: defaultServiceLine }
        : journalLine !== 'all'
          ? { service_line: journalLine }
          : {}),
      ...(journalSearchDebounced.trim() ? { q: journalSearchDebounced.trim() } : {})
    }),
    [journalLine, journalSearchDebounced, lockServiceLine, defaultServiceLine]
  );

  const journalFilters = useMemo(
    () =>
      buildJournalVisitFilters(
        journalScope,
        journalRangeFrom,
        journalRangeTo,
        journalCompanyId,
        journalExtraFilters
      ),
    [
      journalScope,
      journalRangeFrom,
      journalRangeTo,
      journalCompanyId,
      journalExtraFilters
    ]
  );

  const journalQuery = useMemo(
    () =>
      journalFilters
        ? buildJournalVisitQuery(
            journalScope,
            journalRangeFrom,
            journalRangeTo,
            journalCompanyId,
            journalExtraFilters,
            journalPage
          )
        : null,
    [
      journalScope,
      journalRangeFrom,
      journalRangeTo,
      journalCompanyId,
      journalExtraFilters,
      journalFilters,
      journalPage
    ]
  );

  const journalPageCount = useMemo(() => {
    if (!journalTotal) return 0;
    return Math.max(1, Math.ceil(journalTotal / JOURNAL_PAGE_SIZE));
  }, [journalTotal]);

  const journalRangeLabel = useMemo(() => {
    if (!journalTotal) return null;
    const from = journalPage * JOURNAL_PAGE_SIZE + 1;
    const to = Math.min(journalTotal, (journalPage + 1) * JOURNAL_PAGE_SIZE);
    return `${from}–${to}`;
  }, [journalPage, journalTotal]);

  useEffect(() => {
    setJournalPage(0);
  }, [journalFilters]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await CompanyService.getActiveCompanies();
        if (!cancelled && res.success) setJournalCompanies(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (!cancelled) setJournalCompanies([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadJournal = useCallback(async () => {
    if (!journalQuery) {
      setJournalRows([]);
      setJournalTotal(0);
      setJournalLoading(false);
      return;
    }
    setJournalLoading(true);
    try {
      const res = await receptionService.getVisits(journalQuery);
      if (res.success) {
        setJournalRows(Array.isArray(res.data) ? res.data : []);
        const apiTotal = res.pagination?.total;
        if (typeof apiTotal === 'number') {
          setJournalTotal(apiTotal);
        } else {
          const rowCount = Array.isArray(res.data) ? res.data.length : 0;
          setJournalTotal(rowCount);
          if (rowCount >= JOURNAL_PAGE_SIZE && !paginationWarnedRef.current) {
            paginationWarnedRef.current = true;
            toast(
              'warning',
              'Pagination serveur indisponible — redémarrez le backend pour parcourir toutes les pages.'
            );
          }
        }
      } else {
        toast('warning', res.message || 'Données indisponibles');
      }
    } catch (e) {
      toast('danger', e.message || 'Chargement impossible');
    } finally {
      setJournalLoading(false);
    }
  }, [journalQuery, toast]);

  useEffect(() => {
    loadJournal();
  }, [loadJournal]);

  useEffect(() => {
    if (journalPageCount > 0 && journalPage >= journalPageCount) {
      setJournalPage(Math.max(0, journalPageCount - 1));
    }
  }, [journalPage, journalPageCount]);

  const historyListHead = (
    <div className="recv-journal-list__head" aria-hidden="true">
      <span className="recv-journal-list__cell recv-journal-list__cell--time">Passage</span>
      <span className="recv-journal-list__cell recv-journal-list__cell--patient">Patient</span>
      <span className="recv-journal-list__cell recv-journal-list__cell--site">Site</span>
      <span className="recv-journal-list__cell recv-journal-list__cell--consult">Consultation</span>
      <span className="recv-journal-list__cell recv-journal-list__cell--staff">Accueil</span>
    </div>
  );

  const headerTitle = panelTitle ?? 'Journal des passages';
  const headerSubtitle =
    panelSubtitle ??
    'Filtrez par période, société, filière ou recherche ; parcourez toutes les pages ; export CSV sur l’ensemble filtré.';

  const renderJournalPagination = (position = 'bottom') => {
    if (journalLoading || !journalFilters || journalTotal <= 0) return null;
    const multiPage = journalPageCount > 1;
    return (
      <div
        className={`recv-journal-pagination${position === 'top' ? ' mb-3' : ' mt-3'}`}
        aria-label="Pagination du journal"
      >
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={journalPage <= 0 || journalLoading}
          onClick={() => setJournalPage((p) => Math.max(0, p - 1))}
        >
          ← Précédent
        </Button>
        <div className="recv-journal-pagination__info">
          Page <strong>{journalPage + 1}</strong> / {journalPageCount}
          {' · '}
          <strong>{journalTotal}</strong> passage{journalTotal > 1 ? 's' : ''}
          {journalRangeLabel ? (
            <>
              {' '}
              (lignes {journalRangeLabel})
            </>
          ) : null}
          {!multiPage ? (
            <span className="d-block small mt-1">Tous les résultats sont sur cette page.</span>
          ) : null}
        </div>
        <Button
          size="sm"
          variant="outline-secondary"
          disabled={journalPage >= journalPageCount - 1 || journalLoading}
          onClick={() => setJournalPage((p) => Math.min(journalPageCount - 1, p + 1))}
        >
          Suivant →
        </Button>
      </div>
    );
  };

  return (
    <Card className="border-0 recv-dash-card recv-dash-card--elevated recv-panel-card recv-panel-card--journal">
      {!hidePanelHeader ? (
        <Card.Header className="bg-white fw-semibold py-3">
          <div className="fw-semibold">{headerTitle}</div>
          <div className="text-muted small fw-normal mt-1">{headerSubtitle}</div>
        </Card.Header>
      ) : null}
      <Card.Body className={hidePanelHeader ? 'pt-4' : 'pt-3'}>
        <Row className="g-2 mb-3 align-items-end flex-wrap">
          <Col xs={12} md={6} lg={5}>
            <Form.Label className="small text-muted mb-1">Société partenaire</Form.Label>
            <Form.Select
              size="sm"
              value={journalCompanyId}
              onChange={(ev) => setJournalCompanyId(ev.target.value)}
              aria-label="Filtrer par société"
            >
              <option value="all">Toutes les sociétés</option>
              {journalCompanies.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col xs={12} md={6} lg={4}>
            <Form.Label className="small text-muted mb-1 d-block">Période</Form.Label>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <ButtonGroup size="sm">
                <Button
                  variant={journalScope === 'today' ? 'primary' : 'outline-primary'}
                  onClick={() => setJournalScope('today')}
                >
                  Aujourd&apos;hui
                </Button>
                <Button
                  variant={journalScope === 'range' ? 'primary' : 'outline-primary'}
                  onClick={() => {
                    setJournalScope('range');
                    if (!journalRangeFrom || !journalRangeTo) {
                      const d = defaultJournalRangeIsoDates();
                      setJournalRangeFrom(d.from);
                      setJournalRangeTo(d.to);
                    }
                  }}
                >
                  Du… au…
                </Button>
              </ButtonGroup>
              <Button variant="outline-secondary" size="sm" onClick={loadJournal} disabled={journalLoading}>
                Actualiser
              </Button>
            </div>
          </Col>
          {!lockServiceLine ? (
            <Col xs={12} md={6} lg={4}>
              <Form.Label className="small text-muted mb-1">Filière</Form.Label>
              <Form.Select size="sm" value={journalLine} onChange={(ev) => setJournalLine(ev.target.value)}>
                <option value="all">Toutes</option>
                <option value="MEDICAL">Médical</option>
                <option value="DENTAL">Dentaire</option>
                <option value="NURSING">Infirmerie</option>
              </Form.Select>
            </Col>
          ) : null}
          <Col xs={12} md={6} lg={4}>
            <Form.Label className="small text-muted mb-1">Recherche</Form.Label>
            <Form.Control
              size="sm"
              value={journalSearch}
              onChange={(ev) => setJournalSearch(ev.target.value)}
              placeholder="Patient, matricule, #visite…"
            />
          </Col>
        </Row>
        {journalScope === 'range' && (
          <Row className="g-2 align-items-end mb-3 flex-wrap">
            <Col xs={12} sm="auto">
              <Form.Label className="small text-muted mb-1">Du</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={journalRangeFrom}
                onChange={(ev) => setJournalRangeFrom(ev.target.value)}
              />
            </Col>
            <Col xs={12} sm="auto">
              <Form.Label className="small text-muted mb-1">Au</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={journalRangeTo}
                max={isoDateLocal(new Date())}
                onChange={(ev) => setJournalRangeTo(ev.target.value)}
              />
            </Col>
            <Col xs={12} sm="auto" className="d-flex flex-wrap gap-2 align-self-end">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  const { from, to } = defaultJournalRangeIsoDates();
                  setJournalRangeFrom(from);
                  setJournalRangeTo(to);
                }}
              >
                7 derniers jours
              </Button>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  const { from, to } = defaultJournalMonthRangeIsoDates();
                  setJournalRangeFrom(from);
                  setJournalRangeTo(to);
                }}
              >
                1 mois
              </Button>
            </Col>
            {journalRangeFrom && journalRangeTo && journalRangeFrom > journalRangeTo ? (
              <Col xs={12} className="small text-danger mt-1">
                La date « Du » doit être avant ou égale à « Au ».
              </Col>
            ) : null}
          </Row>
        )}
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
          <div className="small text-muted">
            {journalLoading ? (
              'Chargement…'
            ) : !journalFilters ? (
              'Corrigez la plage de dates pour afficher le journal.'
            ) : journalTotal === 0 ? (
              'Aucun passage pour ces filtres.'
            ) : (
              <>
                <strong>{journalTotal}</strong> passage{journalTotal > 1 ? 's trouvés' : ' trouvé'}
                {journalPageCount > 1 ? (
                  <>
                    {' '}
                    · utilisez la pagination ci-dessous ({JOURNAL_PAGE_SIZE} par page)
                  </>
                ) : null}
              </>
            )}
          </div>
          <Button
            size="sm"
            variant="outline-success"
            disabled={!journalFilters || journalLoading || journalTotal === 0}
            onClick={async () => {
              if (!journalFilters) {
                toast('warning', 'Corrigez la plage de dates avant l’export.');
                return;
              }
              try {
                await receptionService.downloadVisitsCsv(journalFilters);
                toast('success', `Export CSV téléchargé (${journalTotal} passage${journalTotal > 1 ? 's' : ''}).`);
              } catch (e) {
                toast('danger', e.message || 'Export CSV impossible');
              }
            }}
          >
            Export CSV (tout filtré)
          </Button>
        </div>
        {journalLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="info" />
          </div>
        ) : !journalFilters ? (
          <p className="text-muted mb-0 small">
            Corrigez la plage « Du… au… » (date de début ≤ date de fin) pour afficher le journal.
          </p>
        ) : journalRows.length === 0 ? (
          <p className="text-muted mb-0 small">Aucune visite enregistrée pour cette période.</p>
        ) : (
          <>
            {renderJournalPagination('top')}
            <div className="recv-journal-list-wrap border rounded bg-white">
              <div className="recv-journal-list" role="list" aria-label="Journal des passages">
                {historyListHead}
                <VisitHistoryTableRows rows={journalRows} />
              </div>
            </div>
            {renderJournalPagination('bottom')}
          </>
        )}
      </Card.Body>
    </Card>
  );
}
